import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import { LocalizationsBlock, validateLocalizations } from '../../features/LocalizationsBlock';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';
import {
  buildEntityFormData,
  mapLocalizationsFromApi,
} from '../../shared/utils/buildEntityFormData.js';

const LIST_PATH = '/others/news';

// Локализация публикации — только текст: изображение и ссылка общие для всех языков
const LOCALIZATION_FIELDS = [
  { name: 'title', label: 'Заголовок', type: 'text', required: true },
  { name: 'shortDescription', label: 'Краткое описание', type: 'textarea' },
];

const emptyForm = {
  title: '',
  shortDescription: '',
  url: '',
  imgPath: '',
};

const getErrorMessage = (error, fallback) =>
  error?.error || error?.response?.data?.error || error?.message || fallback;

/**
 * «СМИ о нас»: создание и редактирование публикации вместе с переводами — одним запросом
 */
const NewsFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(isEdit ? null : emptyForm);
  const [localizations, setLocalizations] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    apiRequests.news
      .getById(id)
      .then((post) => {
        setForm({
          id: post.id,
          title: post.title || '',
          shortDescription: post.shortDescription || '',
          url: post.url || '',
          imgPath: post.imgPath || '',
        });
        setLocalizations(mapLocalizationsFromApi(post.localizations, []));
      })
      .catch((error) => toast.error(getErrorMessage(error, 'Не удалось загрузить публикацию')));
  }, [id]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!form.title.trim()) {
      toast.error('Введите заголовок публикации');
      return;
    }
    if (!isEdit && !mainImage) {
      toast.error('Загрузите изображение публикации');
      return;
    }
    const localizationsError = validateLocalizations(localizations, LOCALIZATION_FIELDS);
    if (localizationsError) {
      toast.error(localizationsError);
      return;
    }

    setSaving(true);
    try {
      const model = { ...form };
      delete model.imgPath;
      const body = buildEntityFormData({ model, mainImage, contentKeys: [], localizations });

      if (isEdit) {
        await apiRequests.news.update(body);
        toast.success('Публикация сохранена');
      } else {
        await apiRequests.news.create(body);
        toast.success('Публикация добавлена');
        setTimeout(() => navigate(LIST_PATH), 1000);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить публикацию'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate(LIST_PATH)}
      saving={saving}
      submitText={isEdit ? 'Сохранить изменения' : 'Добавить публикацию'}
      savingText={isEdit ? 'Сохранение...' : 'Создание...'}
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Заголовок" required>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                placeholder="Например, Forbes: как Alfateam вырос до 100 человек"
              />
            </FormField>

            <FormField
              label="Ссылка на материал"
              hint="Куда ведёт карточка на сайте — страница публикации в издании"
            >
              <Form.Control
                type="url"
                value={form.url}
                onChange={(e) => setField('url', e.target.value)}
                placeholder="https://"
              />
            </FormField>

            <FormField label="Краткое описание">
              <Form.Control
                as="textarea"
                rows={3}
                value={form.shortDescription}
                onChange={(e) => setField('shortDescription', e.target.value)}
                placeholder="О чём материал, пара предложений"
              />
            </FormField>
          </Col>

          <Col lg={4}>
            <FormField label="Изображение" required={!isEdit}>
              <UploadPicture
                onSelect={setMainImage}
                initialValue={form.imgPath || undefined}
                entityType="news"
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <LocalizationsBlock
        value={localizations}
        onChange={setLocalizations}
        fields={LOCALIZATION_FIELDS}
        source={form}
        aiContext="публикация СМИ о компании"
      />
    </FormLayout>
  );
};

export default NewsFormPage;
